/**
 * Integration Editor Panel
 *
 * Form-based editor for INTEGRATION artifacts
 */

import React, { useState, useEffect, useRef } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { integrationApi } from '@/lib/api';
import { Plug, Save, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';

type IntegrationType = 'REST_API' | 'SOAP_API' | 'DATABASE' | 'WEBHOOK' | 'KNOWLEDGE_BASE';
type AuthType = 'NONE' | 'BASIC' | 'BEARER' | 'OAUTH' | 'API_KEY';

interface IntegrationMetadata {
  integrationType: IntegrationType;
  endpoint?: string;
  auth?: {
    type: AuthType;
    credentials?: Record<string, string>;
  };
  headers?: Record<string, string>;
  requestMapping?: string;
  responseMapping?: string;
  operations?: Array<{
    name: string;
    description?: string;
    method?: string;
    path?: string;
    requestExample?: string;
    responseExample?: string;
  }>;
}

const INTEGRATION_TYPE_REVERSE_MAP: Record<string, IntegrationType> = {
  API: 'REST_API',
  REST_API: 'REST_API',
  REST: 'REST_API',
  SOAP_API: 'REST_API',
  SOAP: 'REST_API',
  WEBHOOK: 'WEBHOOK',
  WEB_HOOK: 'WEBHOOK',
  DATABASE: 'DATABASE',
  DB: 'DATABASE',
  KNOWLEDGE_BASE: 'KNOWLEDGE_BASE',
  KNOWLEDGEBASE: 'KNOWLEDGE_BASE'
};

const AUTH_TYPE_REVERSE_MAP: Record<string, AuthType> = {
  NONE: 'NONE',
  none: 'NONE',
  BASIC: 'BASIC',
  basic: 'BASIC',
  BEARER: 'BEARER',
  bearer: 'BEARER',
  API_KEY: 'API_KEY',
  apikey: 'API_KEY',
  'api-key': 'API_KEY',
  OAUTH: 'OAUTH',
  oauth: 'OAUTH',
  OAUTH2: 'OAUTH',
  oauth2: 'OAUTH'
};

const mapIntegrationTypeFromConfig = (value: unknown): IntegrationType => {
  if (typeof value !== 'string') return 'REST_API';
  const normalized = value.toUpperCase();
  return INTEGRATION_TYPE_REVERSE_MAP[normalized] ?? 'REST_API';
};

const mapAuthTypeFromConfig = (value: unknown): AuthType => {
  if (typeof value !== 'string') return 'NONE';
  return AUTH_TYPE_REVERSE_MAP[value] ?? AUTH_TYPE_REVERSE_MAP[value.toUpperCase()] ?? 'NONE';
};

const parseMetadataContent = (raw: string | null | undefined): IntegrationMetadata | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const integrationType = mapIntegrationTypeFromConfig((parsed as any).integrationType);
    const authType = mapAuthTypeFromConfig((parsed as any).auth?.type);

    const headers = (parsed as any).headers;
    const normalizedHeaders = headers && typeof headers === 'object'
      ? Object.entries(headers as Record<string, string>)
        .filter(([key]) => key)
        .reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {})
      : undefined;

    const operationsRaw = Array.isArray((parsed as any).operations) ? (parsed as any).operations : [];
    const operations = operationsRaw
      .map((operation: any) => {
        if (!operation || typeof operation !== 'object' || typeof operation.name !== 'string') return null;
        return {
          name: operation.name,
          description: typeof operation.description === 'string' ? operation.description : undefined,
          method: typeof operation.method === 'string' ? operation.method : undefined,
          path: typeof operation.path === 'string' ? operation.path : undefined,
          requestExample: typeof operation.requestExample === 'string' ? operation.requestExample : undefined,
          responseExample: typeof operation.responseExample === 'string' ? operation.responseExample : undefined
        };
      })
      .filter(Boolean) as IntegrationMetadata['operations'];

    const metadata: IntegrationMetadata = {
      integrationType,
      endpoint: typeof (parsed as any).endpoint === 'string' ? (parsed as any).endpoint : undefined,
      headers: normalizedHeaders && Object.keys(normalizedHeaders).length > 0 ? normalizedHeaders : undefined,
      requestMapping: typeof (parsed as any).requestMapping === 'string' ? (parsed as any).requestMapping : undefined,
      responseMapping: typeof (parsed as any).responseMapping === 'string' ? (parsed as any).responseMapping : undefined,
      operations: operations.length > 0 ? operations : undefined
    };

    if (authType !== 'NONE') {
      metadata.auth = {
        type: authType,
        credentials: (parsed as any).auth?.credentials && typeof (parsed as any).auth.credentials === 'object'
          ? (parsed as any).auth.credentials as Record<string, string>
          : undefined
      };
    }

    return metadata;
  } catch (error) {
    console.warn('[IntegrationEditor] Failed to parse metadata content', error);
    return null;
  }
};

const extractMetadataFromLumaJson = (lumaJson: unknown): IntegrationMetadata | null => {
  let parsed: any = lumaJson;
  if (!parsed) return null;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      console.warn('[IntegrationEditor] Failed to parse LumaJSON content', error);
      return null;
    }
  }

  const cells: any[] | undefined = parsed?.designerFlowDefinition?.cells;
  if (!Array.isArray(cells)) return null;

  const integrationNode = cells.find(cell => cell?.type === 'luma.Integration');
  if (!integrationNode) return null;

  const definition = integrationNode.definition ?? {};
  const config = definition.config ?? {};

  const parameters = typeof config.parameters === 'object' && config.parameters !== null
    ? config.parameters as Record<string, any>
    : undefined;

  const operationsRaw = Array.isArray(parameters?.operations) ? parameters.operations : [];
  const operations = operationsRaw
    .map((operation: any) => {
      if (!operation || typeof operation !== 'object' || typeof operation.name !== 'string') return null;
      return {
        name: operation.name,
        description: typeof operation.description === 'string' ? operation.description : undefined,
        method: typeof operation.method === 'string' ? operation.method : undefined,
        path: typeof operation.path === 'string' ? operation.path : undefined,
        requestExample: typeof operation.requestExample === 'string' ? operation.requestExample : undefined,
        responseExample: typeof operation.responseExample === 'string' ? operation.responseExample : undefined
      };
    })
    .filter(Boolean) as IntegrationMetadata['operations'];

  const authType = mapAuthTypeFromConfig(config.authentication?.type);

  const metadata: IntegrationMetadata = {
    integrationType: mapIntegrationTypeFromConfig(config.type ?? parsed?.facets?.[0]),
    endpoint: typeof config.endpoint === 'string' ? config.endpoint : undefined,
    headers: config.headers && typeof config.headers === 'object' && Object.keys(config.headers).length > 0 ? config.headers : undefined,
    requestMapping: typeof parameters?.requestMapping === 'string' ? parameters.requestMapping : undefined,
    responseMapping: typeof parameters?.responseMapping === 'string' ? parameters.responseMapping : undefined,
    operations: operations.length > 0 ? operations : undefined
  };

  if (authType !== 'NONE') {
    metadata.auth = {
      type: authType,
      credentials: config.authentication?.config && typeof config.authentication.config === 'object'
        ? config.authentication.config as Record<string, string>
        : undefined
    };
  }

  return metadata;
};

export const IntegrationEditorPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifact = useProjectStore((state) => state.updateArtifact);
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const { addToast } = useToastStore();

  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [integrationType, setIntegrationType] = useState<IntegrationType>('REST_API');
  const [endpoint, setEndpoint] = useState('');
  const [authType, setAuthType] = useState<AuthType>('NONE');
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>([]);
  const [requestMapping, setRequestMapping] = useState('');
  const [responseMapping, setResponseMapping] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const operationsRef = useRef<IntegrationMetadata['operations']>(undefined);
  const authCredentialsRef = useRef<Record<string, string> | undefined>(undefined);

  const applyMetadataToState = (metadata: IntegrationMetadata | null) => {
    if (!metadata) {
      setIntegrationType('REST_API');
      setEndpoint('');
      setAuthType('NONE');
      setHeaders([]);
      setRequestMapping('');
      setResponseMapping('');
      operationsRef.current = undefined;
      authCredentialsRef.current = undefined;
      return;
    }

    setIntegrationType(metadata.integrationType ?? 'REST_API');
    setEndpoint(metadata.endpoint || '');
    const nextAuthType = metadata.auth?.type ?? 'NONE';
    setAuthType(nextAuthType);
    authCredentialsRef.current = metadata.auth?.credentials;

    if (metadata.headers) {
      setHeaders(Object.entries(metadata.headers).map(([key, value]) => ({ key, value })));
    } else {
      setHeaders([]);
    }

    setRequestMapping(metadata.requestMapping || '');
    setResponseMapping(metadata.responseMapping || '');
    operationsRef.current = metadata.operations;
  };

  useEffect(() => {
    if (!artifact) return;

    setName(artifact.name);
    setDescription(artifact.description || '');

    const metadataStage = parseMetadataContent(artifact.stages?.LUMAJSON?.content ?? null);
    const derivedMetadata = metadataStage ?? extractMetadataFromLumaJson(artifact.lumaJson);
    applyMetadataToState(derivedMetadata);

    setIsDirty(false);
  }, [artifact?.id, artifact?.stages?.LUMAJSON?.content, artifact?.lumaJson]);

  const handleFieldChange = (setter: React.Dispatch<React.SetStateAction<any>>, value: any) => {
    setter(value);
    setIsDirty(true);
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
    setIsDirty(true);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!activeProjectId || !selectedArtifactId || !artifact) return;

    setIsSaving(true);
    try {
      const normalizedHeaders = headers
        .filter(h => h.key.trim())
        .reduce<Record<string, string>>((acc, current) => {
          acc[current.key] = current.value;
          return acc;
        }, {});

      const metadata: IntegrationMetadata = {
        integrationType,
        endpoint: endpoint.trim() || undefined,
        headers: Object.keys(normalizedHeaders).length > 0 ? normalizedHeaders : undefined,
        requestMapping: requestMapping.trim() || undefined,
        responseMapping: responseMapping.trim() || undefined,
        operations: operationsRef.current && operationsRef.current.length > 0 ? operationsRef.current : undefined
      };

      if (authType !== 'NONE') {
        metadata.auth = {
          type: authType,
          credentials: authCredentialsRef.current
        };
      }

      const response = await integrationApi.updateMetadata(activeProjectId, selectedArtifactId, metadata);

      if (response.warnings?.length) {
        response.warnings.forEach((warning) => addToast('warning', warning));
      }

      await updateArtifact(activeProjectId, selectedArtifactId, {
        name,
        description
      });

      await loadArtifacts(activeProjectId);

      addToast('success', 'Integration metadata updated.');
      setIsDirty(false);
    } catch (error: any) {
      console.error('Failed to save integration:', error);
      addToast('error', `Failed to save integration: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!artifact || artifact.type !== 'INTEGRATION') {
    return (
      <div className="panel-container">
        <div className="panel-content flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">No integration artifact selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-container">
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <Plug className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Integration Editor</h3>
        </div>
      </div>

      <div className="panel-content p-6 overflow-auto">
        <div className="max-w-2xl">
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-1">Edit Integration</h4>
            <p className="text-sm text-muted-foreground">
              Configure external integration settings
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label htmlFor="int-name" className="block text-sm font-medium mb-2">
                Name *
              </label>
              <input
                id="int-name"
                type="text"
                value={name}
                onChange={(e) => handleFieldChange(setName, e.target.value)}
                placeholder="e.g., Ticketing System API"
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="int-identifier" className="block text-sm font-medium mb-2">
                Identifier (Auto-generated)
              </label>
              <input
                id="int-identifier"
                type="text"
                value={artifact.fullIdentifier}
                disabled
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Base: {artifact.identifier} · Suffix: {project?.suffix}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="int-type" className="block text-sm font-medium mb-2">
                  Integration Type
                </label>
                <select
                  id="int-type"
                  value={integrationType}
                  onChange={(e) => handleFieldChange(setIntegrationType, e.target.value as IntegrationType)}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="REST_API">REST API</option>
                  <option value="SOAP_API">SOAP API</option>
                  <option value="DATABASE">Database</option>
                  <option value="WEBHOOK">Webhook</option>
                  <option value="KNOWLEDGE_BASE">Knowledge Base</option>
                </select>
              </div>

              <div>
                <label htmlFor="int-auth" className="block text-sm font-medium mb-2">
                  Authentication
                </label>
                <select
                  id="int-auth"
                  value={authType}
                  onChange={(e) => handleFieldChange(setAuthType, e.target.value as AuthType)}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="NONE">None</option>
                  <option value="BASIC">Basic</option>
                  <option value="BEARER">Bearer Token</option>
                  <option value="OAUTH">OAuth</option>
                  <option value="API_KEY">API Key</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="int-endpoint" className="block text-sm font-medium mb-2">
                Endpoint / Base URL
              </label>
              <input
                id="int-endpoint"
                type="text"
                value={endpoint}
                onChange={(e) => handleFieldChange(setEndpoint, e.target.value)}
                placeholder="https://api.example.com/v1"
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Headers
              </label>
              <div className="space-y-2">
                {headers.map((header, index) => (
                  <div key={index} className="grid grid-cols-10 gap-2">
                    <input
                      type="text"
                      value={header.key}
                      onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                      placeholder="Header Name"
                      className="col-span-4 px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <input
                      type="text"
                      value={header.value}
                      onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                      placeholder="Header Value"
                      className="col-span-5 px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveHeader(index)}
                      className="col-span-1 px-3"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleAddHeader}
                  className="flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Header
                </Button>
              </div>
            </div>

            <div>
              <label htmlFor="int-request" className="block text-sm font-medium mb-2">
                Request Mapping
              </label>
              <textarea
                id="int-request"
                value={requestMapping}
                onChange={(e) => handleFieldChange(setRequestMapping, e.target.value)}
                placeholder="Describe how request payload should be constructed"
                className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="int-response" className="block text-sm font-medium mb-2">
                Response Mapping
              </label>
              <textarea
                id="int-response"
                value={responseMapping}
                onChange={(e) => handleFieldChange(setResponseMapping, e.target.value)}
                placeholder="Describe how to interpret the response payload"
                className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {isDirty && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-100/60 dark:text-amber-500 dark:bg-amber-500/10 px-3 py-2 rounded-md">
                <AlertCircle className="w-4 h-4" />
                <span>You have unsaved changes.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="panel-footer border-t border-border px-6 py-3 flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

